import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-scroll';
import { ArrowRightIcon } from './IconComponents';
import { useTranslation, Trans } from 'react-i18next';

const HeroSection = styled.section`
  height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  position: relative;
  background-image: linear-gradient(rgba(28, 28, 28, 0.75), rgba(28, 28, 28, 0.75)), url('/images/hero.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  overflow: hidden;
`;

const HeroContainer = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  gap: 3rem;

  @media (max-width: 992px) {
    flex-direction: column;
    justify-content: center;
    gap: 2rem;
  }
`;

const HeroContent = styled.div`
  flex: 1;
  color: var(--text-color);
  max-width: 900px;
  text-align: left;

  @media (max-width: 992px) {
    order: 2;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  span {
    color: var(--primary-color);
  }

  @media (max-width: 992px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  margin-bottom: 2rem;

  @media (max-width: 992px) {
    font-size: 1.1rem;
    margin: 0 auto 2rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

const PrimaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  background-color: var(--primary-color);
  color: var(--light-text-color);
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background-color: var(--secondary-color);
    transform: translateY(-2px);
  }

  svg {
    margin-left: 0.5rem;
  }
`;

const SecondaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  background-color: transparent;
  color: var(--text-color);
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-weight: 500;
  border: 2px solid var(--text-color);
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background-color: rgba(51, 51, 51, 0.1);
    transform: translateY(-2px);
  }
`;

const Hero: React.FC = () => {
  const { t, i18n } = useTranslation();

  return (
    <HeroSection id="home">
      <HeroContainer>
        <HeroContent>
          <HeroTitle>
            <Trans i18nKey="hero.title">
              Byte<span>Forge</span>: Innovative Software Solutions for Your Business
            </Trans>
          </HeroTitle>
          <HeroSubtitle>
            {t('hero.subtitle')}
          </HeroSubtitle>
          <ButtonGroup>
            <PrimaryButton
              to="services"
              spy={true}
              smooth={true}
              offset={-70}
              duration={500}
            >
              {t('hero.services_button')} <ArrowRightIcon />
            </PrimaryButton>
            <SecondaryButton
              to="contact"
              spy={true}
              smooth={true}
              offset={-70}
              duration={500}
            >
              {t('hero.contact_button')}
            </SecondaryButton>
          </ButtonGroup>
        </HeroContent>
      </HeroContainer>
    </HeroSection>
  );
};

export default Hero;
